"use client";

import * as React from "react";
import { Activity, Cpu, Thermometer, Zap, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const diagnosticProtocols = [{
  id: "item-1",
  title: "Voltage Drop Analysis",
  icon: Zap,
  content: "We isolate power delivery issues by measuring voltage drops across critical circuit paths under simulated load conditions. This ensures your Miller or Lincoln welder delivers consistent, reliable arc performance without unexpected power fluctuations."
}, {
  id: "item-2",
  title: "Thermal Mapping",
  icon: Thermometer,
  content: "Using high-resolution thermal imaging cameras, we identify overheating components, microscopic shorts, and failing heat sinks on inverter boards before they cause catastrophic failure in your equipment."
}, {
  id: "item-3",
  title: "Signal Integrity Testing",
  icon: Activity,
  content: "Our technicians utilize advanced oscilloscopes to verify PWM signals, gate drive waveforms, and feedback loops. This guarantees precise control and timing in complex Hypertherm plasma cutters and advanced multi-process welders."
}, {
  id: "item-4",
  title: "Component-Level Load Testing",
  icon: Cpu,
  content: "Individual IGBTs, diodes, and capacitors are subjected to rigorous stress tests to verify they meet original manufacturer specifications under maximum operational loads, ensuring long-term reliability after repair."
}];
export default function PrecisionDiagnostics() {
  return <section id="precision-diagnostics" className="dark bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div className="relative order-2 lg:order-1 group">
            <div className="absolute -inset-1 bg-gradient-to-tr from-primary/20 to-transparent rounded-2xl blur-2xl opacity-50 group-hover:opacity-70 transition-opacity duration-500"></div>
            <div className="relative rounded-2xl overflow-hidden border border-border/50 bg-card shadow-2xl shadow-black/50 aspect-[4/3] lg:aspect-square">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591728/img/oscilloscope_thermal_circuit_board.png" alt="Technician using oscilloscope and thermal imaging on a welder circuit board" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent pointer-events-none"></div>
              <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between text-xs font-mono text-primary/80 border-t border-primary/20 pt-4 pointer-events-none">
                <span className="flex items-center gap-2">
                  <span className="size-2 rounded-full bg-primary animate-pulse"></span>
                  SYS_DIAG_ACTIVE
                </span>
                <span>FREQ: 100kHz</span>
              </div>
            </div>
          </div>

          {}
          <div className="order-1 lg:order-2 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 w-fit border border-primary/20">
              <Activity className="size-4" />
              Advanced Testing Protocols
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-foreground">
              Precision Diagnostics
            </h2>
            
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              At Olive Hill Equipment Service, we don't just guess. Our state-of-the-art diagnostic phase ensures every unit is analyzed down to the component level before repair begins, minimizing downtime and preventing future failures.
            </p>

            <div className="mb-10">
              <Accordion type="single" collapsible className="w-full">
                {diagnosticProtocols.map((protocol, i) => <AccordionItem key={protocol.id} value={protocol.id} data-index={i} className="border-border/50">
                    <AccordionTrigger className="text-base font-semibold hover:text-primary hover:no-underline py-5">
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-md bg-muted/50 text-primary border border-border/50">
                          <protocol.icon className="size-5" />
                        </div>
                        {protocol.title}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pb-5 pl-14 pr-4">
                      {protocol.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <div>
              <Button asChild size="lg" className="group">
                <Link to="/RequestServicePage">
                  Request Diagnostic Service
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}